/**
 * "My Approvals" — the in-app equivalent of the emailed magic link, for approvers who
 * would rather work from a list than an inbox. It uses a collection-group query over
 * approvalSteps, which is why firestore.indexes.json declares one.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collectionGroup, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import { formatTs } from './MatterList';

export default function ApprovalQueue() {
  const { user } = useAuth();
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collectionGroup(db, 'approvalSteps'),
      where('eligibleApproverUids', 'array-contains', user.uid),
      where('status', '==', 'pending')
    );
    return onSnapshot(
      q,
      (snap) =>
        setSteps(
          snap.docs.map((d) => ({
            id: d.id,
            matterId: d.ref.parent.parent.id,
            ...d.data(),
          }))
        ),
      (err) => setError(err.message)
    );
  }, [user.uid]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">Awaiting my approval</h1>
      {error && <p className="rounded bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      {steps.length === 0 ? (
        <p className="rounded-lg bg-white dark:bg-slate-900 p-6 text-sm text-slate-500 dark:text-slate-400 shadow">
          Nothing is waiting on you.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg bg-white dark:bg-slate-900 shadow">
          {steps.map((step) => (
            <li key={step.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{step.label}</p>
                {(step.reasons || []).map((reason, i) => (
                  <p key={i} className="text-xs text-firm-700 dark:text-firm-100">
                    · {reason.text}
                  </p>
                ))}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Activated {formatTs(step.activatedAt)}
                  {step.dueAt && ` · due ${formatTs(step.dueAt)}`}
                </p>
              </div>
              <Link
                to={`/approve?matter=${step.matterId}&step=${step.id}`}
                className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400"
              >
                Review
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
